import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

import { spotsBunja } from '@flipster/r230522/original/spots';
import { getSpotTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { calcAmountToQty, calcQtyToAmount, calcSpotPercentToAmount } from '@flipster/utils/calculator/order-preview';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import deferred from '@flipster/utils/deferred';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { spotSymbolSocketBunja, tradeSymbolBunja } from '../../../symbol';
import { commonFormBunja } from '../../common';

export type Side = 'Buy' | 'Sell';
const priceTypes = ['market', 'trigger', 'limit'] as const;
export type PriceType = (typeof priceTypes)[number];
export type OrderType = 'quantity' | 'amount';

interface QuantityState {
  type: 'quantity';
  quantity: string;
}
interface AmountState {
  type: 'amount';
  amount: string;
}
export type OrderState = QuantityState | AmountState;

const orderPercentSliderBuffer = 0.99;

const shouldBufferSliderMax = (side: Side, orderType: OrderType): boolean =>
  (side === 'Buy' && orderType === 'quantity') || (side === 'Sell' && orderType === 'amount');

export const referencePriceBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getTickerAtom, getMarketAtom, getDetailAtom } = bunja.use(spotSymbolSocketBunja);

  const spotDetailAtom = atom((get) => get(getDetailAtom));
  const tickerOrSymbolAtom = atom((get) => get(getTickerAtom) ?? get(getMarketAtom));
  const referencePriceAtom = atom((get) =>
    getSpotTickerDisplayPriceOrDefault(get(spotDetailAtom), get(tickerOrSymbolAtom)),
  );
  const referencePriceReady = deferred();
  function checkReferencePriceReady() {
    const tickerOrSymbol = store.get(tickerOrSymbolAtom);
    if (tickerOrSymbol) referencePriceReady.resolve();
  }

  bunja.effect(() => {
    checkReferencePriceReady();
    return store.sub(tickerOrSymbolAtom, checkReferencePriceReady);
  });

  return {
    referencePriceAtom,
    referencePriceReady,
  };
});
referencePriceBunja.debugLabel = 'spotReferencePriceBunja';

export const spotFormBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, symbolInfoWithDefaultAtom } = bunja.use(tradeSymbolBunja);
  const { referencePriceAtom, referencePriceReady } = bunja.use(referencePriceBunja);

  const { walletsAtom } = bunja.use(walletsBunja);
  const { spotsAtom } = bunja.use(spotsBunja);
  const { getDetailAtom } = bunja.use(spotSymbolSocketBunja);
  const { priceTypeAtom } = bunja.use(commonFormBunja);

  /** field modified check 를 위한 상태 */
  const dirtyAtom = atom({
    orderPercent: false,
    quantity: false,
    amount: false,
    triggerPrice: false,
  });

  /** Buy | Sell */
  const sideAtom = atom<Side>('Buy');
  const limitPriceAtom = atom<string | undefined>(undefined);
  const isBboAtom = atom(false);

  /**
   * triggerPrice 관련 atom
   */
  /** priceType 이 trigger 일 때 trigger price */
  const triggerPriceAtom = atom<string | undefined>(undefined);
  /** 선택한 percent 만큼 trigger price 변경 */
  const changeTriggerPricePercentAtom = atom(null, (get, set, percent: string) => {
    set(dirtyAtom, (prev) => ({ ...prev, triggerPrice: true }));
    const triggerPrice = get(triggerPriceAtom) ?? 0;
    const sumPrice = Num(triggerPrice).dividedBy(100).multipliedBy(percent);
    const pricePrecision = store.get(symbolInfoWithDefaultAtom).pricePrecision;
    set(triggerPriceAtom, Num(triggerPrice).plus(sumPrice).toFixed(pricePrecision, BigNumber.ROUND_HALF_UP));

    segment.track(TradeEvent.SelectPercentageClick, {
      symbol,
      trade_type: 'spot',
      percentage: Num(percent).dividedBy(100).toNumber(),
      percentage_type: 'trigger_price',
    });
  });

  /** trigger price를 reference price 로 초기화 */
  function resetTriggerPrice() {
    referencePriceReady.then(() => {
      // trigger price는 bboPrice로 변경하지 않고 reference price로 유지한다.
      // ref: https://prextech.slack.com/archives/C087D884VEE/p1771935567614249?thread_ts=1771929003.380689&cid=C087D884VEE
      const referencePrice = store.get(referencePriceAtom);
      const precision = store.get(symbolInfoWithDefaultAtom).pricePrecision;
      store.set(
        triggerPriceAtom,
        numberFormatter(referencePrice, { method: 'round-half-up', precision, withComma: false }),
      );
    });
  }

  /** limit price를 bbo price 로 초기화 */
  function resetLimitPrice() {
    referencePriceReady.then(() => {
      const bboPrice = store.get(bboPriceAtom);
      const precision = store.get(symbolInfoWithDefaultAtom).pricePrecision;
      store.set(limitPriceAtom, numberFormatter(bboPrice, { method: 'round-half-up', precision, withComma: false }));
    });
  }

  const bboPriceAtom = atom((get) => {
    const sideType = get(sideAtom);
    const spotDetail = get(getDetailAtom);
    const referencePrice = get(referencePriceAtom);
    return (sideType === 'Buy' ? spotDetail?.askPrice : spotDetail?.bidPrice) ?? referencePrice;
  });

  const calculationPriceAtom = atom((get) => {
    const priceType = get(priceTypeAtom);
    const bboPrice = get(bboPriceAtom);

    switch (priceType) {
      case 'market': {
        return bboPrice;
      }
      case 'limit': {
        const isBbo = get(isBboAtom);
        const limitPrcie = get(limitPriceAtom);
        if (isBbo) return bboPrice;
        return limitPrcie ?? bboPrice;
      }
      case 'trigger': {
        const triggerPrice = get(triggerPriceAtom);
        return triggerPrice ?? bboPrice;
      }
    }
  });

  /**
   * orderState 관련 atom
   */

  const orderStateAtom = atomWithDefault<OrderState>((get) => {
    const initialQtyPrecision = get(symbolInfoWithDefaultAtom)?.tradeQtyPrecision ?? 2;
    return { type: 'quantity', quantity: Num('0').toFixed(initialQtyPrecision) };
  });
  /** order type 변경 시 (Order amount 하단 input의 focus를 옮길 때, focus 된 input의 값을 기반으로 다른 input에 들어갈 값을 계산) */
  const transitOrderTypeAtom = atom(null, (get, set, next: 'quantity' | 'amount') => {
    set(dirtyAtom, (prev) => ({ ...prev, amount: false, quantity: false }));
    const state = get(orderStateAtom);
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    const { type: prev } = state;
    if (prev === next) return;
    const quantityPrecision = symbolInfo.tradeQtyPrecision;
    const price = get(calculationPriceAtom);
    const prevTo = convert(price, { quantityPrecision })[prev](getStateTypeValue(state));
    const stateTypeValue = prevTo[next];

    if (next === 'amount') {
      set(orderStateAtom, { type: 'amount', amount: stateTypeValue });
    } else {
      set(orderStateAtom, { type: 'quantity', quantity: stateTypeValue });
    }
  });
  /** 유형과 값에 따라 상태를 바꿈. 기존 유형과 다른 경우 `transitOrderType`을 수행 한 뒤 값을 바꿈 */
  const changeOrderStateAtom = atom(null, (get, set, orderType: 'quantity' | 'amount', value: string) => {
    const { type: current } = get(orderStateAtom);
    if (current != orderType) set(transitOrderTypeAtom, orderType);

    const finiteValue = value === '' ? '' : Num(value).isFinite() ? value : '0';
    set(orderStateAtom, (state) => ({ ...state, [orderType]: finiteValue }));
    set(dirtyAtom, (prev) => ({ ...prev, [orderType]: true }));
  });

  const changeOrderPercentAtom = atom(null, (get, set, percent: number) => {
    const state = get(orderStateAtom);
    const side = get(sideAtom);
    const applyBuffer = shouldBufferSliderMax(side, state.type);
    const rawMaxOrderAmount = get(maxOrderAmountAtom);
    const rawMaxOrderQty = get(maxOrderQtyAtom);
    const maxOrderAmount = (
      applyBuffer ? rawMaxOrderAmount.multipliedBy(orderPercentSliderBuffer) : rawMaxOrderAmount
    ).toString();
    const maxOrderQty = (
      applyBuffer ? rawMaxOrderQty.multipliedBy(orderPercentSliderBuffer) : rawMaxOrderQty
    ).toString();
    const price = get(calculationPriceAtom);
    const symbolInfo = get(symbolInfoWithDefaultAtom);

    const { qty: quantity, amount } = calcSpotPercentToAmount(
      state.type,
      side,
      maxOrderAmount,
      maxOrderQty,
      price,
      percent,
      symbolInfo.tradeQtyPrecision,
    );

    if (state.type === 'quantity') {
      set(orderStateAtom, { type: 'quantity', quantity });
    } else {
      set(orderStateAtom, { type: 'amount', amount });
    }
    set(dirtyAtom, (prev) => ({ ...prev, orderPercent: true }));
  });

  const quantityAtom = atomWithBigNumber((get) => {
    const state = get(orderStateAtom);
    const price = get(calculationPriceAtom);
    const quantityPrecision = store.get(symbolInfoWithDefaultAtom).tradeQtyPrecision;
    const stateTo = convert(price, { quantityPrecision })[state.type](getStateTypeValue(state));
    const value = Num(stateTo.quantity || 0);
    return value.isNaN() ? Num(0) : value; // '-' 와 같은 NaN 값이 들어오는 경우 0으로 처리
  });

  const amountAtom = atomWithBigNumber((get) => {
    const state = get(orderStateAtom);
    const price = get(calculationPriceAtom);
    const quantityPrecision = store.get(symbolInfoWithDefaultAtom).tradeQtyPrecision;
    const stateTo = convert(price, { quantityPrecision })[state.type](getStateTypeValue(state));
    const value = Num(stateTo.amount || 0);
    return value.isNaN() ? Num(0) : value; // '-' 와 같은 NaN 값이 들어오는 경우 0으로 처리
  });

  const orderPercentAtom = atom<number>((get) => {
    const state = get(orderStateAtom);
    const side = get(sideAtom);
    const price = get(calculationPriceAtom);
    const quantityPrecision = store.get(symbolInfoWithDefaultAtom).tradeQtyPrecision;
    const stateTo = convert(price, { quantityPrecision })[state.type](getStateTypeValue(state));
    const applyBuffer = shouldBufferSliderMax(side, state.type);
    if (state.type === 'quantity') {
      const rawMaxOrderQty = get(maxOrderQtyAtom);
      const base = applyBuffer ? rawMaxOrderQty.multipliedBy(orderPercentSliderBuffer) : rawMaxOrderQty;
      return stateTo.orderPercent(base.toString());
    }
    const rawMaxOrderAmount = get(maxOrderAmountAtom);
    const base = applyBuffer ? rawMaxOrderAmount.multipliedBy(orderPercentSliderBuffer) : rawMaxOrderAmount;
    return stateTo.orderPercent(base.toString());
  });

  const takerFeeRateAtom = atom((get) => {
    const spots = get(spotsAtom);
    const takerFeeRate = spots?.[symbol]?.takerFeeRate ?? '0';
    return takerFeeRate;
  });

  const availableFundsAtom = atomWithBigNumber((get) => {
    const sideType = get(sideAtom);
    const wallet = get(walletsAtom);
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    const takerFeeRate = get(takerFeeRateAtom);
    const quoteCurrency = wallet?.[symbolInfo.quoteCurrency ?? ''];
    const baseCurrency = wallet?.[symbolInfo.baseCurrency ?? ''];
    const price = get(calculationPriceAtom);

    switch (sideType) {
      case 'Buy': {
        if (!quoteCurrency) return Num(0);
        return BigNumber.max(0, Num(quoteCurrency.walletBalanceWithdrawable).multipliedBy(Num(1).minus(takerFeeRate)));
      }
      case 'Sell': {
        if (!baseCurrency) return Num(0);
        return BigNumber.max(0, Num(baseCurrency.walletBalanceWithdrawable).multipliedBy(price));
      }
      default: {
        return Num(0);
      }
    }
  });

  const tradeQtyPrecisionAtom = atom((get) => {
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    return symbolInfo?.tradeQtyPrecision ?? 2;
  });

  const lotSizeAtom = atomWithBigNumber((get) => {
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    return Num(symbolInfo?.unitOrderQty ?? 0);
  });

  const notionalMaxOrderAmountAtom = atomWithBigNumber((get) => {
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    return Num(symbolInfo?.notionalMaxOrderAmount ?? 0);
  });

  const notionalMinOrderAmountAtom = atomWithBigNumber((get) => {
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    return Num(symbolInfo?.notionalMinOrderAmount ?? 0);
  });

  const remainingDailyLimitAmountAtom = atomWithBigNumber((get) => {
    const side = get(sideAtom);
    const spot = get(spotsAtom);
    const spotSymbol = spot?.[symbol];

    switch (side) {
      case 'Buy':
        return spotSymbol?.buyRemainingDailyLimitAmount ? Num(spotSymbol.buyRemainingDailyLimitAmount) : undefined;
      case 'Sell':
        return spotSymbol?.sellRemainingDailyLimitAmount ? Num(spotSymbol.sellRemainingDailyLimitAmount) : undefined;
      default:
        return undefined;
    }
  });

  const buyNotionalSlippageMultiplierAtom = atom((get) => {
    const side = get(sideAtom);
    if (side !== 'Buy') return Num(1);
    const slippage = get(symbolInfoWithDefaultAtom)?.defaultMaxSlippage;
    return slippage ? Num(1).minus(slippage) : Num(1);
  });

  const maxOrderAmountAtom = atomWithBigNumber((get) => {
    const availableFunds = get(availableFundsAtom);

    const remainingDailyLimitAmount = get(remainingDailyLimitAmountAtom);
    const notionalMaxOrderAmount = get(notionalMaxOrderAmountAtom);
    const slippageMultiplier = get(buyNotionalSlippageMultiplierAtom);

    const notionalOrDailyCap = remainingDailyLimitAmount
      ? BigNumber.min(notionalMaxOrderAmount, remainingDailyLimitAmount)
      : notionalMaxOrderAmount;

    return BigNumber.min(notionalOrDailyCap.multipliedBy(slippageMultiplier), availableFunds);
  });

  const minOrderAmountAtom = atomWithBigNumber((get) => {
    const calcPrice = get(calculationPriceAtom);
    const minOrderAmount = get(notionalMinOrderAmountAtom);
    const lotSize = get(lotSizeAtom);

    return BigNumber.max(
      minOrderAmount,
      lotSize.multipliedBy(calcPrice).decimalPlaces(UsdtPrecision, BigNumber.ROUND_UP),
    );
  });

  const availableQtyAtom = atomWithBigNumber((get) => {
    const wallet = get(walletsAtom);
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    const baseCurrency = wallet?.[symbolInfo.baseCurrency ?? ''];
    const tradeQtyPrecision = get(tradeQtyPrecisionAtom);
    const availableUsdt = get(availableFundsAtom);
    const side = get(sideAtom);

    switch (side) {
      case 'Buy': {
        const calcPrice = get(calculationPriceAtom);
        if (Num(calcPrice).isLessThanOrEqualTo(0)) return Num(0);
        return Num(availableUsdt).div(calcPrice).decimalPlaces(tradeQtyPrecision, BigNumber.ROUND_DOWN);
      }
      case 'Sell': {
        if (!baseCurrency) return Num(0);
        return BigNumber.max(0, baseCurrency.walletBalanceWithdrawable);
      }
      default: {
        return Num(0);
      }
    }
  });

  const maxOrderQtyAtom = atomWithBigNumber((get) => {
    const calcPrice = get(calculationPriceAtom);
    const availableQty = get(availableQtyAtom);
    const tradeQtyPrecision = get(tradeQtyPrecisionAtom);
    const notionalMaxOrderAmount = get(notionalMaxOrderAmountAtom);
    const remainingDailyLimitAmount = get(remainingDailyLimitAmountAtom);
    const slippageMultiplier = get(buyNotionalSlippageMultiplierAtom);

    const notionalMaxOrderQty = notionalMaxOrderAmount
      .multipliedBy(slippageMultiplier)
      .div(calcPrice)
      .decimalPlaces(tradeQtyPrecision, BigNumber.ROUND_DOWN);
    const remainingDailyLimitQty = remainingDailyLimitAmount
      ?.div(calcPrice)
      .decimalPlaces(tradeQtyPrecision, BigNumber.ROUND_DOWN);

    return BigNumber.min(
      remainingDailyLimitQty ? BigNumber.min(notionalMaxOrderQty, remainingDailyLimitQty) : notionalMaxOrderQty,
      availableQty,
    );
  });

  const minOrderQtyAtom = atomWithBigNumber((get) => {
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    const minOrderAmount = get(minOrderAmountAtom);
    const calcPrice = get(calculationPriceAtom);
    const tradeQtyPrecision = get(tradeQtyPrecisionAtom);

    const minUnit = symbolInfo?.unitOrderQty ?? '0';

    return BigNumber.max(
      minUnit,
      Num(minOrderAmount).dividedBy(calcPrice).decimalPlaces(tradeQtyPrecision, BigNumber.ROUND_UP),
    );
  });

  const availableBaseCurrencyAtom = atom<string>((get) => {
    const sideType = get(sideAtom);
    const wallet = get(walletsAtom);
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    const takerFeeRate = get(takerFeeRateAtom);
    const quoteCurrency = wallet?.[symbolInfo.quoteCurrency ?? ''];
    const baseCurrency = wallet?.[symbolInfo.baseCurrency ?? ''];
    const price = get(calculationPriceAtom);

    if (sideType === 'Buy') {
      if (!quoteCurrency) {
        return '0';
      }
      if (price === '0') {
        return '0';
      }
      return BigNumber.max(
        0,
        Num(quoteCurrency.walletBalanceWithdrawable).multipliedBy(Num(1).minus(takerFeeRate)).dividedBy(price),
      ).toString();
    }
    if (!baseCurrency) {
      return '0';
    }
    return BigNumber.max(0, baseCurrency.walletBalanceWithdrawable).toString();
  });

  const positionUnitAtom = atom((get) => {
    const symbolInfo = get(symbolInfoWithDefaultAtom);
    return symbolInfo?.baseCurrency ?? 'USDT';
  });

  /** 심볼 변경 시(spotFormBunja가 tradeSymbolBunja에 의존하여 재생성) 및 priceType 전환 시 price 초기화 */
  bunja.effect(() => {
    const priceType = store.get(priceTypeAtom);
    if (priceType === 'limit') resetLimitPrice();
    if (priceType === 'trigger') resetTriggerPrice();

    return store.sub(priceTypeAtom, () => {
      const priceType = store.get(priceTypeAtom);
      if (priceType === 'limit') resetLimitPrice();
      if (priceType === 'trigger') resetTriggerPrice();
    });
  });

  return {
    dirtyAtom,

    sideAtom,
    priceTypeAtom,

    triggerPriceAtom,
    changeTriggerPricePercentAtom,
    resetTriggerPrice,

    orderStateAtom,
    transitOrderTypeAtom,
    changeOrderStateAtom,
    changeOrderPercentAtom,

    orderPercentAtom,
    quantityAtom,
    amountAtom,

    takerFeeRateAtom,
    availableUsdtAtom: availableFundsAtom,
    maxOrderAmountAtom,
    minOrderAmountAtom,
    availableBaseCurrencyAtom,
    availableQtyAtom,
    maxOrderQtyAtom,
    minOrderQtyAtom,

    tradeQtyPrecisionAtom,
    positionUnitAtom,

    isBboAtom,
    bboPriceAtom,
    referencePriceAtom,
    limitPriceAtom,
    calculationPriceAtom,
    resetLimitPrice,
  };
});
spotFormBunja.debugLabel = 'spotFormBunja';

interface ConvertOptions {
  quantityPrecision?: number;
}

export function convert(
  price: string,
  options?: ConvertOptions,
): Record<
  'quantity' | 'amount',
  (value: string) => {
    quantity: string;
    amount: string;
    orderPercent: (availableFunds: string) => number;
  }
> {
  const { quantityPrecision = 2 } = options ?? {};
  return {
    quantity(quantity: string) {
      return {
        quantity: Num(quantity).toFixed(quantityPrecision),
        amount: calcQtyToAmount(quantity, price),
        orderPercent(maxOrderQty: string) {
          if (Num(maxOrderQty).isZero()) return 0;
          const rate = Num(quantity).div(maxOrderQty);
          return Math.min(100, Math.max(0, Math.round(rate.times(100).toNumber())));
        },
      };
    },
    amount(amount: string) {
      return {
        quantity: calcAmountToQty(amount, price, quantityPrecision),
        amount: Num(amount).toFixed(UsdtPrecision),
        orderPercent(availableFunds: string) {
          if (Num(availableFunds).isZero()) return 0;
          const rate = Num(amount).div(availableFunds); // prob clamp
          return Math.min(Math.round(rate.times(100).toNumber()), 100);
        },
      };
    },
  };
}

export function getStateTypeValue(state: OrderState) {
  switch (state.type) {
    case 'amount':
      return state.amount;
    case 'quantity':
      return state.quantity;
  }
}
